import { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage, LANGUAGE_OPTIONS } from '@/contexts/LanguageContext';
import HelpDrawer from '@/components/HelpDrawer';
import HelpGuide from '@/components/HelpGuide';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import NLQueryBar from '@/components/NLQueryBar';
import OnboardingTour from '@/components/OnboardingTour';
import CommandPalette from '@/components/CommandPalette';
import getHelpContent from '@/data/helpContent';
import getTourSteps from '@/data/tourSteps';
import {
  Plane, LayoutDashboard, Building2, MapPin, Users, FileText,
  Shield, Settings, LogOut, ChevronLeft, ChevronDown, Search, User,
  ScrollText, Menu, GraduationCap, BookOpen, ClipboardCheck,
  Award, Calendar, Sun, Moon, AlertTriangle, PlaneTakeoff,
  Wrench, ShieldAlert, CloudSun, CreditCard, ShoppingCart, BarChart3,
  FolderOpen, Plug, UserCircle, CalendarClock, TrendingUp, X, Languages,
  UserPlus, ListChecks, UsersRound, DollarSign, HeartPulse, CalendarDays, UserX, Megaphone, Trash2, Flag,
  BookMarked, ShieldCheck, AlertOctagon, Network, Activity, Banknote,
  FileArchive, Scale, Receipt, Landmark, Percent, Calculator, RefreshCw, PiggyBank,
  BookOpenCheck, FileSpreadsheet, Coins, HandCoins, CalendarRange, Store, ReceiptText, PieChart,
  ClipboardPen, FileSignature, Sparkles, Gauge, MapPinned
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import NotificationBell from '@/components/NotificationBell';

const NAV_ITEMS_RAW = [
  { section: 'nav.overview', items: [
    { labelKey: 'nav.dashboard', icon: LayoutDashboard, path: '/', roles: ['admin', 'finance', 'auditor'] },
    { labelKey: 'nav.myPortal', icon: UserCircle, path: '/my-portal', roles: ['student'] },
    { labelKey: 'nav.analytics', icon: TrendingUp, path: '/analytics', roles: ['admin', 'finance'] },
  ]},
  { section: 'nav.studentsTraining', items: [
    { labelKey: 'nav.students', icon: GraduationCap, path: '/students', roles: ['admin', 'instructor', 'partner_instructor'] },
    { labelKey: 'nav.studentProgress', icon: TrendingUp, path: '/student-progress', roles: ['admin'] },
    { labelKey: 'nav.academicLifecycle', icon: BookOpen, path: '/academic-lifecycle', roles: ['admin'] },
    { labelKey: 'nav.trainingPrograms', icon: Award, path: '/programs', roles: ['admin'] },
    { labelKey: 'nav.courses', icon: BookOpen, path: '/courses', roles: ['admin', 'student'] },
    { labelKey: 'nav.courseDependencyMap', icon: Network, path: '/course-dependency-map', roles: ['admin', 'auditor', 'instructor'] },
    { labelKey: 'nav.classes', icon: Calendar, path: '/classes', roles: ['admin', 'student'] },
    { labelKey: 'nav.batches', icon: UsersRound, path: '/batches', roles: ['admin'] },
    { labelKey: 'nav.batchTimetable', icon: CalendarRange, path: '/batch-timetable', roles: ['admin', 'instructor'] },
    { labelKey: 'nav.exams', icon: ClipboardCheck, path: '/exams', roles: ['admin'] },
    { labelKey: 'nav.skillTests', icon: ClipboardCheck, path: '/skill-tests', roles: ['admin'] },
    { labelKey: 'nav.lessonDebrief', icon: ClipboardPen, path: '/debrief', roles: ['admin', 'instructor'] },
    { labelKey: 'nav.trainingRecords', icon: FileSignature, path: '/training-records', roles: ['admin', 'instructor', 'student', 'auditor'] },
    { labelKey: 'nav.certifications', icon: Award, path: '/certifications', roles: ['admin', 'student'] },
    { labelKey: 'nav.alumni', icon: Users, path: '/alumni', roles: ['admin', 'instructor', 'finance'] },
    { labelKey: 'nav.licenses', icon: ShieldCheck, path: '/licenses', roles: ['admin', 'instructor', 'finance', 'student'] },
  ]},
  { section: 'nav.schedulingOps', items: [
    { labelKey: 'nav.scheduling', icon: CalendarClock, path: '/scheduling', roles: ['admin', 'instructor', 'student', 'partner_instructor'] },
    { labelKey: 'nav.flightDispatch', icon: PlaneTakeoff, path: '/flight-dispatch', roles: ['admin', 'instructor', 'partner_instructor'] },
    { labelKey: 'nav.operationsBoard', icon: MapPinned, path: '/operations-board', roles: ['admin', 'instructor', 'auditor'] },
    { labelKey: 'nav.autoScheduler', icon: Sparkles, path: '/auto-scheduler', roles: ['admin', 'instructor'] },
    { labelKey: 'nav.utilization', icon: Gauge, path: '/utilization', roles: ['admin', 'instructor', 'auditor'] },
    { labelKey: 'nav.operationsTasks', icon: ListChecks, path: '/tasks', roles: ['admin'] },
  ]},
  { section: 'nav.flightOps', items: [
    { labelKey: 'nav.aircraft', icon: Plane, path: '/aircraft', roles: ['admin'] },
    { labelKey: 'nav.flightLogs', icon: PlaneTakeoff, path: '/flight-logs', roles: ['admin', 'instructor', 'student', 'partner_instructor'] },
    { labelKey: 'nav.maintenance', icon: Wrench, path: '/maintenance', roles: ['admin'] },
    { labelKey: 'nav.workOrders', icon: Wrench, path: '/work-orders', roles: ['admin', 'instructor', 'auditor'] },
    { labelKey: 'nav.stageChecks', icon: ClipboardCheck, path: '/stage-checks', roles: ['admin', 'instructor', 'auditor'] },
    { labelKey: 'nav.safety', icon: ShieldAlert, path: '/safety', roles: ['admin', 'auditor'] },
    { labelKey: 'nav.weather', icon: CloudSun, path: '/weather', roles: ['admin', 'instructor', 'student'] },
    { labelKey: 'nav.weatherMinima', icon: CloudSun, path: '/weather-minima', roles: ['admin', 'instructor', 'auditor'] },
  ]},
  { section: 'nav.crm', items: [
    { labelKey: 'nav.crmLeads', icon: UserPlus, path: '/crm', roles: ['admin'] },
  ]},
  { section: 'nav.financeAccounting', items: [
    { group: 'nav.grpOverview', labelKey: 'nav.financeHome', icon: LayoutDashboard, path: '/finance-home', roles: ['admin', 'finance'] },
    { group: 'nav.grpReceivables', labelKey: 'nav.studentFinance', icon: DollarSign, path: '/student-finance', roles: ['admin', 'finance'] },
    { group: 'nav.grpReceivables', labelKey: 'nav.billing', icon: CreditCard, path: '/billing', roles: ['admin', 'finance', 'student'] },
    { group: 'nav.grpReceivables', labelKey: 'nav.paymentPlans', icon: CalendarRange, path: '/payment-plans', roles: ['admin', 'finance'] },
    { group: 'nav.grpReceivables', labelKey: 'nav.recurringInvoices', icon: RefreshCw, path: '/recurring-invoices', roles: ['admin', 'finance'] },
    { group: 'nav.grpReceivables', labelKey: 'nav.invoiceDunning', icon: AlertTriangle, path: '/invoice-dunning', roles: ['admin', 'finance', 'auditor'] },
    { group: 'nav.grpReceivables', labelKey: 'nav.legacyPayments', icon: Banknote, path: '/finance/legacy-payments', roles: ['admin', 'finance'] },
    { group: 'nav.grpReceivables', labelKey: 'nav.feeCalculator', icon: Calculator, path: '/fee-calculator', roles: ['admin', 'finance'] },
    { group: 'nav.grpPayables', labelKey: 'nav.expenses', icon: Receipt, path: '/expenses', roles: ['admin', 'finance'] },
    { group: 'nav.grpPayables', labelKey: 'nav.vendors', icon: Store, path: '/vendors', roles: ['admin', 'finance'] },
    { group: 'nav.grpPayables', labelKey: 'nav.vendorBills', icon: ReceiptText, path: '/vendor-bills', roles: ['admin', 'finance'] },
    { group: 'nav.grpPayables', labelKey: 'nav.procurement', icon: ShoppingCart, path: '/procurement', roles: ['admin', 'finance'] },
    { group: 'nav.grpPayables', labelKey: 'nav.pettyCash', icon: Coins, path: '/petty-cash', roles: ['admin', 'finance'] },
    { group: 'nav.grpPayables', labelKey: 'nav.payroll', icon: HandCoins, path: '/payroll', roles: ['admin', 'finance'] },
    { group: 'nav.grpAccounting', labelKey: 'nav.accounting', icon: Landmark, path: '/accounting', roles: ['admin', 'finance', 'auditor'] },
    { group: 'nav.grpAccounting', labelKey: 'nav.openingBalances', icon: BookOpenCheck, path: '/opening-balances', roles: ['admin'] },
    { group: 'nav.grpAccounting', labelKey: 'nav.budgets', icon: PiggyBank, path: '/budgets', roles: ['admin', 'finance'] },
    { group: 'nav.grpAccounting', labelKey: 'nav.revenue', icon: TrendingUp, path: '/revenue', roles: ['admin', 'finance'] },
    { group: 'nav.grpAccounting', labelKey: 'nav.incomeStatement', icon: FileSpreadsheet, path: '/income-statement', roles: ['admin', 'finance'] },
    { group: 'nav.grpTax', labelKey: 'nav.vatZatca', icon: Percent, path: '/vat-reports', roles: ['admin', 'finance'] },
    { group: 'nav.grpTax', labelKey: 'nav.zakat', icon: Scale, path: '/zakat', roles: ['admin', 'finance', 'auditor'] },
    { group: 'nav.grpTax', labelKey: 'nav.wht', icon: Percent, path: '/wht', roles: ['admin', 'finance', 'auditor'] },
    { group: 'nav.grpTax', labelKey: 'nav.taxCalendar', icon: CalendarDays, path: '/tax-calendar', roles: ['admin', 'finance', 'auditor'] },
    { group: 'nav.grpReports', labelKey: 'nav.financeReports', icon: BarChart3, path: '/finance-reports', roles: ['admin', 'finance'] },
    { group: 'nav.grpReports', labelKey: 'nav.reportsDashboard', icon: PieChart, path: '/reports-dashboard', roles: ['admin', 'finance'] },
    { group: 'nav.grpReports', labelKey: 'nav.reports', icon: FileText, path: '/reports', roles: ['admin', 'finance', 'auditor'] },
  ]},
  { section: 'nav.organization', items: [
    { labelKey: 'nav.staffDirectory', icon: Users, path: '/staff', roles: ['admin'] },
    { labelKey: 'nav.departments', icon: Building2, path: '/departments', roles: ['admin'] },
    { labelKey: 'nav.leaves', icon: CalendarDays, path: '/leaves', roles: ['admin', 'finance'] },
    { labelKey: 'nav.absences', icon: UserX, path: '/absences', roles: ['admin'] },
    { labelKey: 'nav.contracts', icon: FileText, path: '/contracts', roles: ['admin', 'finance'] },
    { labelKey: 'nav.salaryScale', icon: DollarSign, path: '/salary-scale', roles: ['admin'] },
    { labelKey: 'nav.disciplinary', icon: ShieldAlert, path: '/disciplinary', roles: ['admin', 'instructor', 'finance', 'student'] },
    { labelKey: 'nav.violations', icon: Flag, path: '/violations', roles: ['admin', 'instructor'] },
    { labelKey: 'nav.adminComms', icon: Megaphone, path: '/admin-comms', roles: ['admin', 'instructor', 'finance', 'student'] },
    { labelKey: 'nav.instructorPortal', icon: Plane, path: '/instructor-portal', roles: ['instructor'] },
    { labelKey: 'nav.locations', icon: MapPin, path: '/company/locations', roles: ['admin'] },
    { labelKey: 'nav.companySettings', icon: Building2, path: '/company/settings', roles: ['admin'] },
  ]},
  { section: 'nav.complianceReports', items: [
    { labelKey: 'nav.complianceReports', icon: FileText, path: '/compliance-reports', roles: ['admin', 'auditor'] },
    { labelKey: 'nav.medicalCompliance', icon: HeartPulse, path: '/medical-alerts', roles: ['admin'] },
    { labelKey: 'nav.documentExpiry', icon: AlertTriangle, path: '/document-expiry', roles: ['admin'] },
    { labelKey: 'nav.aviationReports', icon: FileText, path: '/aviation-reports', roles: ['admin', 'auditor'] },
    { labelKey: 'nav.documents', icon: FolderOpen, path: '/documents', roles: ['admin', 'finance', 'instructor', 'auditor'] },
    { labelKey: 'nav.complianceDocs', icon: Shield, path: '/company/documents', roles: ['admin', 'auditor'] },
    { labelKey: 'nav.auditLogs', icon: ScrollText, path: '/audit-logs', roles: ['admin', 'auditor'] },
    { labelKey: 'nav.auditExport', icon: FileArchive, path: '/audit-export', roles: ['admin', 'auditor'] },
  ]},
  { section: 'nav.system', items: [
    { labelKey: 'nav.partnerPortal', icon: Building2, path: '/partner-portal', roles: ['partner_admin', 'partner_instructor'] },
    { labelKey: 'nav.partnerAcademies', icon: Building2, path: '/partner-academies', roles: ['admin', 'finance', 'auditor'] },
    { labelKey: 'nav.userGuide', icon: BookMarked, path: '/user-guide', roles: ['admin', 'instructor', 'finance', 'auditor', 'student'] },
    { labelKey: 'nav.securityMonitor', icon: ShieldAlert, path: '/security', roles: ['admin'] },
    { labelKey: 'nav.integrations', icon: Plug, path: '/integrations', roles: ['admin'] },
    { labelKey: 'nav.notifications', icon: AlertTriangle, path: '/notifications', roles: ['admin'] },
    { labelKey: 'nav.userManagement', icon: Settings, path: '/users', roles: ['admin'] },
    { labelKey: 'nav.clientErrors', icon: AlertOctagon, path: '/client-errors', roles: ['admin', 'auditor'] },
    { labelKey: 'nav.dataCleanup', icon: Trash2, path: '/data-cleanup', roles: ['admin'] },
    { labelKey: 'nav.dataTools', icon: Wrench, path: '/admin/data-tools', roles: ['admin'] },
    { labelKey: 'nav.gradeChangeRequests', icon: ClipboardCheck, path: '/grade-change-requests', roles: ['admin', 'instructor', 'auditor'] },
    { labelKey: 'nav.canaryHistory', icon: Activity, path: '/canary-history', roles: ['admin'] },
    { labelKey: 'nav.walkthroughsCms', icon: BookMarked, path: '/walkthroughs-cms', roles: ['admin'] },
    { labelKey: 'nav.schedulerLogs', icon: Activity, path: '/scheduler-logs', roles: ['admin', 'auditor'] },
  ]},
];

export default function DashboardLayout() {
  const { user, logout, axiosAuth } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [density, setDensity] = useState(() => localStorage.getItem('sidebar_density') || 'comfortable');
  useEffect(() => { if (user?.sidebar_density && !localStorage.getItem('sidebar_density')) setDensity(user.sidebar_density); }, [user?.sidebar_density]);
  const changeDensity = (next) => {
    setDensity(next); localStorage.setItem('sidebar_density', next);
    axiosAuth().put('/auth/preferences', { sidebar_density: next }).catch(() => {});
  };
  const compact = density === 'compact';
  const sz = {
    section: compact ? 'text-[11px] text-cyan-400/90' : 'text-sm text-cyan-400',
    group: compact ? 'text-[10px] font-medium text-muted-foreground/70 hover:text-foreground border-white/10 hover:border-cyan-500/40' : 'text-xs font-semibold text-amber-400 hover:text-amber-300 border-amber-400/40 hover:border-amber-400/80',
    item: compact ? 'text-sm py-2' : 'text-[15px] py-2.5',
    itemIdle: compact ? 'text-muted-foreground hover:text-foreground hover:bg-white/[0.04]' : 'text-foreground/80 hover:text-foreground hover:bg-white/[0.05]',
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [activeGuide, setActiveGuide] = useState(null);
  const [showGuidesOnOpen, setShowGuidesOnOpen] = useState(false);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [pendingGcrCount, setPendingGcrCount] = useState(0);
  const [pendingCertCount, setPendingCertCount] = useState(0);
  const [foldedGroups, setFoldedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_folded_groups') || '{}'); } catch { return {}; }
  });
  const toggleGroup = (group) => setFoldedGroups((prev) => {
    const next = { ...prev, [group]: !prev[group] };
    localStorage.setItem('sidebar_folded_groups', JSON.stringify(next));
    return next;
  });
  const { theme, toggleTheme } = useTheme();
  const { language, changeLanguage } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  // Fetch company logo
  useEffect(() => {
    const img = new window.Image();
    img.onload = () => setCompanyLogo(`${API_BASE}/api/company/logo?v=${Date.now()}`);
    img.onerror = () => setCompanyLogo(null);
    img.src = `${API_BASE}/api/company/logo?v=${Date.now()}`;
  }, [API_BASE]);

  // Track tour completion in memory (not sessionStorage for security)
  const tourDoneRef = useRef(new Set());

  // Check if onboarding tour should show
  useEffect(() => {
    if (!user) return;
    const tourKey = `${user.user_id || user.email}`;
    // Skip if already completed in this session or permanently
    if (tourDoneRef.current.has(tourKey) || user.tour_completed) {
      setShowTour(false);
      return;
    }
    const timer = setTimeout(() => setShowTour(true), 1500);
    return () => clearTimeout(timer);
  }, [user]);

  // Ctrl+K / Cmd+K to open command palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Poll pending-review count for the sidebar badge (admins + dept heads)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await axiosAuth().get('/admin-comms/forms/can-review');
        if (!cancelled) setPendingReviewCount(res.data?.pending_count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000); // refresh every 60s
    return () => { cancelled = true; clearInterval(id); };
  }, [user, axiosAuth, location.pathname]);

  // Poll pending external-certificate count for the Certifications badge (admins only).
  useEffect(() => {
    if (!user || user.role !== 'admin') { setPendingCertCount(0); return undefined; }
    let cancelled = false;
    const fetchCerts = async () => {
      try {
        const res = await axiosAuth().get('/certifications/pending-queue/count');
        if (!cancelled) setPendingCertCount(res.data?.count || 0);
      } catch { /* silently ignore */ }
    };
    fetchCerts();
    const id = setInterval(fetchCerts, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, axiosAuth, location.pathname]);

  // Poll Grade-Change-Request inbox count (admin / chief_instructor only).
  useEffect(() => {
    if (!user) return;
    const role = user.role;
    if (role !== 'admin' && role !== 'chief_instructor') { setPendingGcrCount(0); return; }
    let cancelled = false;
    const fetchGcr = async () => {
      try {
        const res = await axiosAuth().get('/grade-change-requests/inbox/count');
        if (!cancelled) setPendingGcrCount(res.data?.count || 0);
      } catch { /* silently ignore */ }
    };
    fetchGcr();
    const id = setInterval(fetchGcr, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, axiosAuth, location.pathname]);


  const completeTour = (dontShowAgain) => {
    setShowTour(false);
    if (user) {
      tourDoneRef.current.add(`${user.user_id || user.email}`);
      if (dontShowAgain) {
        // Persist to DB so it never shows again
        try { axiosAuth().put('/auth/preferences', { tour_completed: true }); } catch {}
      }
    }
  };

  const userRole = user?.role || 'student';
  const currentTourSteps = getTourSteps()[userRole] || getTourSteps().student;

  const filteredNav = NAV_ITEMS_RAW.map(section => ({
    ...section,
    sectionLabel: t(section.section),
    items: section.items.filter(item => item.roles.includes(user?.role || 'student')).map(item => ({
      ...item,
      label: t(item.labelKey),
    }))
  })).filter(section => section.items.length > 0);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const sidebarContent = (
    <>
      {/* Logo Section */}
      <div className="h-16 border-b border-border/50 flex items-center px-4 gap-3">
        {companyLogo ? (
          <div className="h-10 max-w-[7rem] rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 flex items-center justify-center flex-shrink-0 border border-cyan-500/20 overflow-hidden px-1.5">
            <img src={companyLogo} alt="Logo" className="max-h-8 w-auto object-contain" data-testid="sidebar-company-logo" />
          </div>
        ) : (
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 flex items-center justify-center flex-shrink-0 border border-cyan-500/20">
            <Plane className="w-5 h-5 text-cyan-400" data-testid="sidebar-plane-icon" />
          </div>
        )}
        {!collapsed && (
          <div className="overflow-hidden animate-fade-in">
            <h1 className="text-base font-bold tracking-tight text-foreground" style={{ fontFamily: 'Outfit' }}>
              SAGER
            </h1>
            <p className="text-[10px] text-cyan-400 font-medium tracking-widest uppercase">Aviation</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto px-2">
        {filteredNav.map((section, sectionIdx) => (
          <div key={section.section} className={sectionIdx > 0 ? 'mt-6' : ''}>
            {!collapsed && (
              <p className={`px-3 mb-2 font-bold uppercase tracking-[0.12em] ${sz.section}`} style={{ fontFamily: 'Outfit' }}>
                {section.sectionLabel}
              </p>
            )}
            <div className="space-y-1">
              {section.items.map((item, itemIdx) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const showGroup = !collapsed && item.group && item.group !== section.items[itemIdx - 1]?.group;
                const folded = !collapsed && item.group && foldedGroups[item.group];
                const badgeCount = item.path === '/admin-comms'
                  ? pendingReviewCount
                  : item.path === '/grade-change-requests'
                    ? pendingGcrCount
                    : item.path === '/certifications'
                      ? pendingCertCount
                      : 0;
                const groupSlug = item.group ? item.group.replace('nav.grp', '').toLowerCase() : '';
                return (
                  <div key={item.path}>
                  {showGroup && (
                    <button
                      type="button"
                      data-testid={`nav-group-${groupSlug}`}
                      aria-expanded={!folded}
                      onClick={() => toggleGroup(item.group)}
                      className={`w-full flex items-center justify-between pl-3 pr-2 py-1 uppercase tracking-[0.1em] border-l-2 ml-2 transition-colors ${sz.group} ${itemIdx > 0 ? 'mt-3' : ''} mb-1`}
                    >
                      <span>{t(item.group)}</span>
                      <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${folded ? '-rotate-90' : ''}`} />
                    </button>
                  )}
                  {(!folded || active) && (
                  <button
                    data-testid={`nav-${item.path.replace(/\//g, '-').replace(/^-/, '') || 'dashboard'}`}
                    onClick={() => { navigate(item.path); setMobileOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 rounded-lg transition-all duration-200 ${sz.item} ${
                      active
                        ? 'sidebar-active font-semibold'
                        : sz.itemIdle
                    } ${collapsed ? 'justify-center px-2' : ''}`}
                  >
                    <div className="relative flex-shrink-0">
                      <Icon className={`w-[18px] h-[18px] ${active ? 'text-cyan-400' : ''}`} strokeWidth={1.75} />
                      {badgeCount > 0 && collapsed && (
                        <span data-testid="sidebar-badge-collapsed" className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                          {badgeCount > 9 ? '9+' : badgeCount}
                        </span>
                      )}
                    </div>
                    {!collapsed && (
                      <>
                        <span className="truncate flex-1 text-left">{item.label}</span>
                        {badgeCount > 0 && (
                          <span data-testid={`sidebar-badge-${item.path.replace(/\//g, '-').replace(/^-/, '')}`} className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                  )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User Section */}
      <div className="border-t border-border/50 p-3">
        {!collapsed && (
          <div className="flex items-center justify-between px-2 pb-2 mb-1 border-b border-border/40" data-testid="sidebar-density">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Text size</span>
            <div className="flex rounded-md overflow-hidden border border-border/60">
              {[['compact', 'Compact'], ['comfortable', 'Comfortable']].map(([k, l]) => (
                <button key={k} onClick={() => changeDensity(k)} className={`px-2 py-0.5 text-[11px] transition-colors ${density === k ? 'bg-cyan-500/20 text-cyan-300 font-semibold' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`density-${k}`} aria-pressed={density === k}>{l}</button>
              ))}
            </div>
          </div>
        )}
        <div className={`flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.04] transition-colors ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 flex items-center justify-center flex-shrink-0 border border-cyan-500/20">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-lg object-cover" />
            ) : (
              <User className="w-4 h-4 text-cyan-400" />
            )}
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 overflow-hidden">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-[11px] text-cyan-400 font-medium uppercase tracking-wide">{user?.role}</p>
              </div>
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="p-2 hover:bg-red-500/10 rounded-lg text-muted-foreground hover:text-red-400 transition-all duration-200"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden" data-testid="dashboard-layout">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex flex-col border-r border-border/50 bg-background/95 backdrop-blur-2xl transition-all duration-300 relative ${
          collapsed ? 'w-[72px]' : 'w-64'
        }`}
      >
        {sidebarContent}

        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-secondary border border-border rounded-full flex items-center justify-center hover:bg-cyan-500/10 hover:border-cyan-500/30 transition-all duration-200 z-20"
        >
          <ChevronLeft className={`w-3 h-3 text-muted-foreground transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm" 
            onClick={() => setMobileOpen(false)} 
          />
          <aside className="absolute left-0 top-0 h-full w-72 bg-background border-r border-border/50 flex flex-col animate-slide-in">
            {sidebarContent}
          </aside>
          <button
            onClick={() => setMobileOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-16 border-b border-border/50 flex items-center justify-between px-4 md:px-6 bg-background/95 backdrop-blur-2xl flex-shrink-0">
          <div className="flex items-center gap-4">
            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 hover:bg-white/[0.04] rounded-lg transition-colors"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Search Bar - opens Command Palette */}
            <button
              onClick={() => setCommandPaletteOpen(true)}
              className="hidden md:flex items-center gap-2.5 px-4 py-2.5 bg-secondary/50 rounded-xl border border-border/50 w-72 hover:border-border transition-colors group cursor-pointer"
              data-testid="global-search"
            >
              <Search className="w-4 h-4 text-muted-foreground group-hover:text-foreground/70 transition-colors" />
              <span className="text-sm text-muted-foreground flex-1 text-left">Search anything...</span>
              <kbd className="hidden lg:inline-flex h-5 select-none items-center gap-1 rounded border border-border/50 bg-background/50 px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                Ctrl K
              </kbd>
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* Ask-your-data trigger (admin/auditor only). Shown inline in the
                top bar — previously floated bottom-right which felt detached. */}
            <NLQueryBar />
            {/* Language Selector Dropdown */}
            <div className="relative">
              <button
                data-testid="language-toggle"
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="p-2.5 hover:bg-white/[0.04] rounded-xl text-muted-foreground hover:text-foreground transition-all duration-200 flex items-center gap-1.5"
                title="Select Language"
              >
                <Languages className="w-[18px] h-[18px]" />
                <span className="text-xs font-medium hidden sm:inline">
                  {LANGUAGE_OPTIONS.find(l => l.code === language)?.code.toUpperCase() || 'EN'}
                </span>
              </button>
              
              {/* Dropdown Menu */}
              {langDropdownOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setLangDropdownOpen(false)} 
                  />
                  <div className="absolute right-0 top-full mt-2 w-48 bg-background border border-border/50 rounded-xl shadow-lg z-50 py-1 animate-fade-in">
                    {LANGUAGE_OPTIONS.map((lang) => (
                      <button
                        key={lang.code}
                        data-testid={`lang-option-${lang.code}`}
                        onClick={() => {
                          changeLanguage(lang.code);
                          setLangDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-white/[0.04] transition-colors ${
                          language === lang.code ? 'bg-cyan-500/10 text-cyan-400' : 'text-foreground'
                        }`}
                      >
                        <span className="text-base">{lang.flag}</span>
                        <div className="flex-1">
                          <span className="text-sm font-medium">{lang.nativeName}</span>
                          <span className="text-xs text-muted-foreground ml-2">({lang.name})</span>
                        </div>
                        {language === lang.code && (
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {/* Theme Toggle */}
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              className="p-2.5 hover:bg-white/[0.04] rounded-xl text-muted-foreground hover:text-foreground transition-all duration-200"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {theme === 'dark' ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
            </button>
            
            {/* Notifications */}
            <NotificationBell />
            
            {/* User Avatar (Mobile) */}
            <div className="md:hidden w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 flex items-center justify-center border border-cyan-500/20">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-lg object-cover" />
              ) : (
                <User className="w-4 h-4 text-cyan-400" />
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <div className="animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Floating Help Drawer */}
      {!activeGuide && <HelpDrawer {...(getHelpContent(user?.role)[location.pathname] || {})} onStartGuide={(id) => { setShowGuidesOnOpen(false); setActiveGuide(id); }} autoOpenGuides={showGuidesOnOpen} />}
      {/* Floating Help Guide */}
      {activeGuide && <HelpGuide activeGuide={activeGuide} onClose={() => { setActiveGuide(null); setShowGuidesOnOpen(true); }} />}

      {/* PWA install prompt (appears on supported browsers when installable) */}
      <PWAInstallPrompt />

      {/* Onboarding Tour */}
      {showTour && <OnboardingTour steps={currentTourSteps} onComplete={completeTour} />}

      {/* Command Palette */}
      <CommandPalette isOpen={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
    </div>
  );
}
